<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { definePage } from 'unplugin-vue-router/runtime'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from 'primevue/usetoast'
import { useConfirm } from 'primevue/useconfirm'

import PrimeToggleSwitch from 'primevue/toggleswitch'
import PrimeButton from 'primevue/button'

import * as Sentry from '@sentry/vue'

import {
  ELEMENT_TYPE,
  LAYOUT_TYPE,
  type LocoKitLayoutPageStatus,
  type LocoKitDataset,
  type LocoKitElement,
  type LocoKitElementButton,
  type LocoKitElementDataList,
  type LocoKitElementDataTable,
  type LocoKitElementFormRecord,
  type LocoKitElementQuickFilters,
  type ResolvedQuickFilterVia,
  type LocoKitElementTextBlock,
  type LocoKitLayoutElement,
  type LocoKitElementDataField,
  type LocoKitElementDataTitle
} from '@/declarations'
import {
  type LckTableRow,
  type LckTableRowData,
  type LckTableRowOffline,
  LckTableViewColumn
} from '@/services/lck-api/definitions'
import { isEditableColumn } from '@/services/lck-utils/columns'
import FormRecord from '@/components/form-record.vue'
import DataTable from '@/components/data-table.vue'
import DataList from '@/components/data-list.vue'
import DataField from '@/components/data-field.vue'
import TextBlock from '@/components/text-block.vue'
import FilterOverlay from '@/components/filter-overlay.vue'
import QuickFilters from '@/components/quick-filters.vue'
import DropdownButton from '@/components/dropdown-button.vue'
import { useStoreData } from '@/stores/data'
import { useStoreApp } from '@/stores/app'
import type { Paginated } from '@feathersjs/feathers'
import { lckHelpers, lckServices } from '@/services/lck-api'
import { pagesStructure } from './pages'
import { formatRowData } from '@/services/lck-api/helpers'
import { filterRecord, getCurrentFilters, type Filter } from '@/services/lck-utils/filter'
import { useStoreAuth } from '@/stores/auth'

const route = useRoute()
const router = useRouter()
const {
  dataState,
  addData,
  searchData,
  removeData,
  getReferencedIdsBySearch,
  getSnapshotRowsByReference
} = useStoreData()
const { appState } = useStoreApp()
const { authState } = useStoreAuth()
const { locale, t } = useI18n()
const toast = useToast()
const confirm = useConfirm()

definePage({
  meta: {
    requireAuth: true
  }
})

const groupId = computed(() => route.params.groupId as string)
const pageId = computed(() => route.params.pageId as string)
const pageStructure = computed(() => pagesStructure[pageId.value])
const pageStatus = ref<LocoKitLayoutPageStatus>('DEFAULT')
const pageColumnKey = ref()

type DatasetMultiRecord = {
  loading: boolean
  error: Error | null
  records: LckTableRow[]
  total: number
  limit: number
  skip: number
  sort?: Record<string, 1 | -1>
  filters?: Filter[]
  quickFilters?: Filter[]
  /**
   * Reverse-search id constraint: restrict the dataset to these row ids.
   * `null` means no constraint, `[]` means "no match" (empty result).
   */
  idConstraint?: string[] | null
}
type DatasetSingleRecord = {
  loading: boolean
  error: Error | null
  record: Partial<LckTableRow>
}
/**
 * Page state
 */
const datasets = ref<Record<string, DatasetMultiRecord | DatasetSingleRecord>>({})

/**
 * Selection context: values stashed on record select (eg the selected
 * profile id under '{currentProfileId}'), usable as filter wildcards
 * and to feed related datasets (`relatedTo`).
 */
const selectionContext = ref<Record<string, string>>({})

const displayOnlyOfflineData = ref(!appState.isOnline)
watch(
  () => appState.isOnline,
  (isOnline) => {
    displayOnlyOfflineData.value = !isOnline
  }
)

/**
 * Load a specific dataset
 *
 * Can be a multi or single one
 */
async function loadDataset(datasetKey: string, limit: number = 20, skip: number = 0) {
  const currentDataset = getDataset(datasetKey)
  /**
   * Check if all preload route params - if any - exist
   *
   * If not, will only init the dataset in datasets ref
   */
  let preloadRouteParamsOK = true
  currentDataset.preloadRouteParams?.forEach((preloadRouteParams) => {
    if (!route.query[preloadRouteParams]) preloadRouteParamsOK = false
  })

  /**
   * if dataset is a multi record
   */
  if (currentDataset.multi) {
    // init the dataset if not already init + loading = true
    if (!datasets.value[datasetKey])
      datasets.value[datasetKey] = {
        loading: true,
        error: null,
        records: [],
        total: 0,
        limit,
        skip,
        filters: [],
        sort: currentDataset.sort
      }
    else datasets.value[datasetKey].loading = true

    const currentDatasetMultiRecord = datasets.value[datasetKey] as DatasetMultiRecord
    const currentTableViewId = currentDataset.table_view_id
    if (!preloadRouteParamsOK) return

    /**
     * Load the current dataset only if browser is online AND user don't want offline data,
     * with specific pagination / skip / limit inputs ?
     *
     * We normally have all information for the current dataset structure
     * in the data store
     */
    /**
     * Combine quick filters (always-visible inputs)
     * with the generic overlay filters
     */
    const combinedFilters = [
      ...(currentDatasetMultiRecord.quickFilters || []),
      ...(currentDatasetMultiRecord.filters || [])
    ]
    const wildcards = {
      '{userId}': authState.user?.id,
      '{groupId}': groupId,
      ...selectionContext.value
    }

    /**
     * Related dataset: resolved client-side from the offline snapshot,
     * restricted to the rows referencing the current selection
     * (eg the boats / signing rules of the selected profile).
     *
     * Snapshot-based in both modes (the snapshot is always loaded at login),
     * as matching a relation by referenced id isn't expressed by the API path.
     */
    if (currentDataset.relatedTo) {
      const referenceId = selectionContext.value[currentDataset.relatedTo.wildcard]
      const relatedRows = referenceId
        ? getSnapshotRowsByReference(
            groupId.value,
            currentTableViewId,
            currentDataset.relatedTo.referenceFieldId,
            referenceId
          ).map((r) => ({ ...r, fromServer: true }))
        : []
      const relatedRowsFiltered = relatedRows.filter((r) =>
        filterRecord(combinedFilters, wildcards, r)
      )
      currentDatasetMultiRecord.total = relatedRowsFiltered.length
      currentDatasetMultiRecord.records = relatedRowsFiltered.filter(
        (_r, index) => index >= skip && index < skip + limit
      )
      datasets.value[datasetKey].loading = false
      return
    }

    /**
     * Reverse-search id constraint (resolved by onSubmitQuickFilters):
     * null = no constraint, [] = no match.
     */
    const idConstraint = currentDatasetMultiRecord.idConstraint

    if (!displayOnlyOfflineData.value && appState.isOnline) {
      const filters = getCurrentFilters(combinedFilters, wildcards)
      const records = (await lckServices.tableRow.find({
        query: {
          table_view_id: currentTableViewId,
          $lckGroupId: groupId.value,
          $limit: limit || currentDatasetMultiRecord.limit,
          $skip: skip || currentDatasetMultiRecord.skip,
          $sort: currentDataset.sort,
          ...(idConstraint != null ? { id: { $in: idConstraint } } : {}),
          ...filters
        }
      })) as Paginated<LckTableRow>
      currentDatasetMultiRecord.records = records.data
      currentDatasetMultiRecord.total = records.total
      currentDatasetMultiRecord.skip = records.skip
    } else {
      /**
       * in other cases, where displayOnlyOffline is true OR appState is not online,
       * display offline data
       */
      const offlineRecords: (Partial<LckTableRow> & {
        fromServer?: boolean,
        offlineIndex?: number,
        offline?: boolean
      })[] = dataState.offlineDataServer[groupId.value]
        ?.[currentDataset.table_view_id]?.map(r => ({ ...r, fromServer: true })) ?? []
      offlineRecords.concat(
        dataState.offlineDataUser[groupId.value]
          ?.filter(r => r.originalData.table_view_id === currentTableViewId)
          .map((r, index) => ({
            ...r.originalData,
            offlineIndex: index,
            offline: true
          }))
      )

      const offlineRecordsFiltered = offlineRecords
        .filter(r => idConstraint == null || idConstraint.includes(r.id as string))
        .filter(r => filterRecord(combinedFilters, wildcards, r))

      currentDatasetMultiRecord.total = offlineRecordsFiltered?.length || 0
      if (currentDatasetMultiRecord.total === 0) {
        currentDatasetMultiRecord.records = []
        datasets.value[datasetKey].loading = false
        return
      }

      currentDatasetMultiRecord.records = offlineRecordsFiltered
        /**
         * Extract the limit / skip records from offline
         */
        .filter((_or, index) => index >= skip && index < skip + limit)
        .map((or, index) => ({
          ...or,
          fromServer: true
        }))

    }
  } else {
    // init the dataset if not already init + loading = true
    if (!datasets.value[datasetKey])
      datasets.value[datasetKey] = {
        loading: true,
        error: null,
        record: {}
      }
    else datasets.value[datasetKey].loading = true
    if (!preloadRouteParamsOK) return

    // if an id is set, load it if we are online
    const { id, offline, offlineIndex } = route.query
    if (id && appState.isOnline) {
      const record = (await lckServices.tableRow.get(id as string)) as LckTableRow
        ; (datasets.value[datasetKey] as DatasetSingleRecord).record = record
    } else if (appState.isOnline) {
      const records = (await lckServices.tableRow.find({
        query: {
          table_view_id: currentDataset.table_view_id,
          $lckGroupId: groupId.value,
          $limit: 1,
          $skip: 0
        }
      })) as Paginated<LckTableRow>
      if (records.total === 1)
        (datasets.value[datasetKey] as DatasetSingleRecord).record = records.data[0]
      else throw new Error('Multi records found instead of single one.')
    } else if (offline === 'true') {
      ; (datasets.value[datasetKey] as DatasetSingleRecord).record =
        dataState.offlineDataUser[groupId.value][offlineIndex as any as number]?.originalData
    }
  }

  datasets.value[datasetKey].loading = false
}

/**
 * Utils functions
 */
function getElement(elementKey: string): LocoKitElement {
  return pageStructure?.value.elements[elementKey]
}
function getDataset(datasetKey: string): LocoKitDataset {
  return pageStructure?.value.datasets[datasetKey]
}

/**
 * Data table / list functions
 * * management of input
 * * pagination event
 *
 */
function getDataTableInput(elementKey: string) {
  const element = getElement(elementKey) as LocoKitElementDataTable | LocoKitElementDataList
  const matchingDatasetDefinition = pageStructure?.value.datasets[element.datasetKey]
  const matchingDataset = datasets.value[element.datasetKey]
  const fields = dataState?.views?.[groupId.value]
    ?.find((d) => d.id === matchingDatasetDefinition.table_view_id)
    ?.columns.filter((c) => c.displayed)

  return {
    dataset: matchingDataset,
    fields,
    settings: {
      displayDetailButton: true,
      deleteAllowed: element.settings.deleteAllowed,
      duplicateAllowed: element.settings.duplicateAllowed
    }
  }
}
const getDataListInput = getDataTableInput

function getDataTitleInput(elementKey: string) {
  const {
    datasetKey,
    settings,
  } = getElement(elementKey) as LocoKitElementDataTitle
  const matchingDataset = datasets.value[datasetKey] as DatasetSingleRecord

  return {
    ...settings,
    text: {
      en: [matchingDataset.record?.text],
      fr: [matchingDataset.record?.text],
    }
  }
}


function getFilterOverlayInput(elementKey: string) {
  const { datasetKey } = getElement(elementKey) as LocoKitElementDataTable | LocoKitElementDataList
  const matchingDatasetDefinition = pageStructure?.value.datasets[datasetKey]
  const fields =
    dataState?.views?.[groupId.value]
      ?.find((d) => d.id === matchingDatasetDefinition.table_view_id)
      ?.columns?.filter((c) => c.displayed) || []

  return {
    fields
  }
}

function onSubmitFilters(elementKey: string, filters: Filter[]) {
  const { datasetKey } = getElement(elementKey) as LocoKitElementDataTable | LocoKitElementDataList
    ; (datasets.value[datasetKey] as DatasetMultiRecord).filters = filters
  loadDataset(datasetKey)
}

function onResetFilters(elementKey: string) {
  const { datasetKey } = getElement(elementKey) as LocoKitElementDataTable | LocoKitElementDataList
    ; (datasets.value[datasetKey] as DatasetMultiRecord).filters = []
  loadDataset(datasetKey)
}

/**
 * Quick filters
 * * provide the declared fields + the dataset columns to resolve their type
 * * on submit, store them apart from the overlay filters and reload
 */
function getQuickFiltersInput(elementKey: string) {
  const element = getElement(elementKey) as LocoKitElementQuickFilters
  const matchingDatasetDefinition = pageStructure?.value.datasets[element.datasetKey]
  const columns =
    dataState?.views?.[groupId.value]?.find(
      (d) => d.id === matchingDatasetDefinition.table_view_id
    )?.columns || []

  return {
    fields: element.settings.fields,
    columns
  }
}

function onSubmitQuickFilters(
  elementKey: string,
  payload: { filters: Filter[]; via: ResolvedQuickFilterVia[] }
) {
  const { datasetKey } = getElement(elementKey) as LocoKitElementQuickFilters
  const dataset = datasets.value[datasetKey] as DatasetMultiRecord

  // direct filters apply on the dataset itself
  dataset.quickFilters = payload.filters

  /**
   * Reverse-search: resolve each `via` criterion to the set of ids it
   * references in the bound dataset, then intersect them (AND).
   * No active `via` => no constraint (null).
   */
  if (payload.via.length === 0) {
    dataset.idConstraint = null
  } else {
    let constraint: string[] | null = null
    payload.via.forEach((via) => {
      const ids = getReferencedIdsBySearch(
        groupId.value,
        via.tableViewId,
        via.searchFieldId,
        via.value,
        via.referenceFieldId
      )
      constraint =
        constraint === null ? ids : constraint.filter((id) => ids.includes(id))
    })
    dataset.idConstraint = constraint
  }

  loadDataset(datasetKey)
}

function getTextBlockInput(elementKey: string) {
  const { settings } = getElement(elementKey) as LocoKitElementTextBlock
  return settings
}

function onPage(elementKey: string, $event: any) {
  const { datasetKey } = getElement(elementKey) as LocoKitElementDataTable | LocoKitElementDataList
  loadDataset(datasetKey, 20, $event.page * 20)
}

/**
 * Form records
 * * management of input
 * * management of suggestions
 * * record of a form
 *
 */
const autocompleteSuggestions = ref<{ label: string; value: string }[]>([])
const formRecordSaving = ref(false)

function getFormRecordInput(layoutElement: LocoKitLayoutElement) {
  const { datasetKey, settings } = getElement(layoutElement.elementKey) as LocoKitElementFormRecord
  const matchingDatasetDefinition = pageStructure?.value.datasets[datasetKey]
  const matchingDataset = datasets.value[datasetKey] as DatasetSingleRecord

  const tableView = dataState.views[groupId.value]?.find(
    (tv) => tv.id === matchingDatasetDefinition.table_view_id
  )
  const formFieldsDisplayed = tableView?.columns
    // ?.filter((f) => isFieldDisplayed(f))
    ?.filter((f) => isEditableColumn(f, true))
    .toSorted((a, b) => a.position - b.position)

  return {
    fields: formFieldsDisplayed,
    suggestions: autocompleteSuggestions.value,
    loading: formRecordSaving.value,
    record: matchingDataset.record,
    readOnly: settings?.readOnly,
    displayDeleteButton: settings?.displayDeleteButton,
    liveSaving: settings?.liveSaving
  }
}

const disabledButtons = ref<Record<string, boolean>>({})
function disableButton(layoutElement: LocoKitLayoutElement) {
  disabledButtons.value[layoutElement.elementKey] = true
}
function enableButton(layoutElement: LocoKitLayoutElement) {
  disabledButtons.value[layoutElement.elementKey] = false
}
function getButtonInput(layoutElement: LocoKitLayoutElement) {
  const element = getElement(layoutElement.elementKey) as LocoKitElementButton
  const attributes = {
    label: element.label?.[locale.value],
    class: element.class,
    icon: element.icon,
    disabled: true
  }
  if (element.settings?.disabledDisplay) {
    if (!element.settings?.disabledDisplayDatasetKey)
      console.warn(
        `Element ${layoutElement.elementKey} seems misconfigured (disabledDisplayDatasetKey is missing). Please check your configuration : `,
        element.settings
      )
    else {
      const currentData = (
        datasets.value[element.settings.disabledDisplayDatasetKey] as DatasetSingleRecord
      ).record
      const currentDataTargetValue =
        currentData.data?.[element.settings.disabledDisplayDatasetFieldId]

      attributes.disabled =
        currentData === null ||
        currentDataTargetValue === null ||
        currentDataTargetValue === undefined ||
        currentDataTargetValue === element.settings.disabledDisplayDatasetFieldValue ||
        disabledButtons.value[layoutElement.elementKey]
    }
  } else attributes.disabled = false

  return attributes
}

/**
 * Retrieve data for a DATA_FIELD,
 * need the field (column) object and the data related to this field.
 */
function getDataFieldInput(element: LocoKitLayoutElement) {
  const {
    datasetKey,
    settings,
    label,
    class: elementClass
  } = getElement(element.elementKey) as LocoKitElementDataField
  const matchingDatasetDefinition = pageStructure?.value.datasets[datasetKey]
  const matchingDataset = datasets.value[datasetKey] as DatasetSingleRecord

  const tableView = dataState.views[groupId.value]?.find(
    (tv) => tv.id === matchingDatasetDefinition.table_view_id
  )
  const field = tableView?.columns?.find((f) => f.id === settings.fieldId) as LckTableViewColumn

  return {
    field,
    data: matchingDataset.record.data?.[settings.fieldId],
    class: elementClass,
    label: label?.[locale.value]
  }
}

async function onComplete(formField: LckTableViewColumn, { query }: { query: string }) {
  autocompleteSuggestions.value = await searchData(
    groupId.value,
    formField.settings.tableId as string,
    query,
    formField.foreign_filter as Record<string, string> | undefined
  )
}

async function onSaveFormRecord(
  layoutElement: LocoKitLayoutElement,
  data: Record<string, LckTableRowData>,
  originalRecord: Partial<LckTableRowOffline> = {}
) {
  formRecordSaving.value = true
  const element = getElement(layoutElement.elementKey) as LocoKitElementFormRecord
  try {
    /**
     * Retrieve element settings
     */
    const dataset = getDataset(element.datasetKey)
    const matchingDatasetDefinition = pageStructure?.value.datasets[element.datasetKey]
    const tableView = dataState.views[groupId.value]?.find(
      (tv) => tv.id === matchingDatasetDefinition.table_view_id
    )
    const formFieldsDisplayed = tableView?.columns
      // ?.filter((f) => isFieldDisplayed(f))
      ?.filter((f) => isEditableColumn(f, true))
      .toSorted((a, b) => a.position - b.position)

    /**
     * If we have to override, let's do it
     */
    if (element.settings.overrideData) {
      Object.keys(element.settings.overrideData).forEach(dataKey => {
        data[dataKey] = element.settings.overrideData[dataKey]
      })
    }
    /**
     * Depending we need to store updates before sending them (for offline data)
     *
     * we add data, or we send directly our updates to the backend
     */
    const pageIsOffline = pageStructure.value.offline?.ability

    let record: LckTableRow
    if (pageIsOffline) {
      await addData(
        {
          ...originalRecord,
          data,
          table_view_id: dataset.table_view_id
        },
        groupId.value,
        formFieldsDisplayed
      )
    } else {
      /**
       * if an id exists, it's a patch
       */
      const formattedData = formatRowData(data || {}, formFieldsDisplayed)
      if (originalRecord.id) {
        record = await lckServices.tableRow.patch(originalRecord.id as string, {
          data: formattedData,
          table_view_id: dataset.table_view_id,
          $lckGroupId: groupId.value
        })
      } else {
        /**
         * else, we create a new record
         */
        record = await lckServices.tableRow.create({
          data: formattedData,
          table_view_id: dataset.table_view_id,
          $lckGroupId: groupId.value
        })
      }
    }
    if (element.settings.displayToast) {
      const summary = element.settings.toast?.success.title[locale.value]
      const detail = element.settings.toast?.success.message[locale.value]
      toast.add({
        severity: 'info',
        summary,
        detail,
        life: 5000
      })
    }

    if (element.settings.postAction) {
      await Promise.all(
        element.settings.postAction.map(async (action) => {
          switch (action) {
            case 'ROUTER_PUSH':
              router.push('/group/' + groupId.value + '/page/' + element.settings.nextPageId)
              break
            case 'RELOAD_DATASET':
              if (!element.settings.postActionDatasetToReload)
                console.warn(
                  `Element ${layoutElement.elementKey} seems misconfigured (postActionDatasetToReload is missing). Please check your configuration : `,
                  element.settings
                )
              else {
                await Promise.all(
                  element.settings.postActionDatasetToReload.map((dataset) => loadDataset(dataset))
                )
              }
              break
            case 'FORM_CLOSE':
              pageStatus.value = 'DEFAULT'
              updateFocusPageData(layoutElement)
              break
            case 'UPDATE_DATASET':
              const matchingDataset = datasets.value[element.datasetKey] as DatasetSingleRecord
              if (record) {
                matchingDataset.record = record
              }
              break
          }
        })
      )
    }
  } catch (error) {
    Sentry.captureException(error)
    const summary = element.settings.toast?.error.title[locale.value]
    const detail = element.settings.toast?.error.message[locale.value]
    toast.add({
      severity: 'error',
      summary,
      detail,
      life: 5000
    })
  }
  formRecordSaving.value = false
}

/**
 * Save immediately updates on a record.
 *
 * Available only for online elements
 * and for PATCH operations
 *
 * @param layoutElement
 * @param fieldId
 * @param newValue
 */
async function onUpdateFieldFormRecord(
  layoutElement: LocoKitLayoutElement,
  fieldId: string,
  newValue: LckTableRowData,
  originalRecord: Partial<LckTableRowOffline> = {}
) {
  formRecordSaving.value = true
  const element = getElement(layoutElement.elementKey) as LocoKitElementFormRecord
  try {
    const data = {
      [fieldId]: newValue
    }
    if (element.settings.overrideData) {
      Object.keys(element.settings.overrideData).forEach(dataKey => {
        data[dataKey] = element.settings.overrideData[dataKey]
      })
    }

    /**
     * Retrieve element settings
     */
    const dataset = getDataset(element.datasetKey)
    const matchingDatasetDefinition = pageStructure?.value.datasets[element.datasetKey]
    const tableView = dataState.views[groupId.value]?.find(
      (tv) => tv.id === matchingDatasetDefinition.table_view_id
    )
    const formFieldsDisplayed = tableView?.columns
      // ?.filter((f) => isFieldDisplayed(f))
      ?.filter((f) => isEditableColumn(f, true))
      .toSorted((a, b) => a.position - b.position)

    /**
     * Check the page is not an offline one, if so trigger an error
     */
    const pageIsOffline = pageStructure.value.offline?.ability
    if (pageIsOffline)
      throw new Error(
        `Element ${layoutElement.elementKey} seems misconfigured (update-field unavailable for offline page).`
      )

    const formattedData = formatRowData(data || {}, formFieldsDisplayed)
    if (originalRecord.id) {
      ; (datasets.value[element.datasetKey] as DatasetSingleRecord).record =
        await lckServices.tableRow.patch(originalRecord.id as string, {
          data: formattedData,
          table_view_id: dataset.table_view_id,
          $lckGroupId: groupId.value
        })
    }
  } catch (error) {
    Sentry.captureException(error)
    const summary = element.settings.toast?.error.title[locale.value]
    const detail = element.settings.toast?.error.message[locale.value]
    toast.add({
      severity: 'error',
      summary,
      detail,
      life: 5000
    })
  }
  formRecordSaving.value = false
}

/**
 * Trigerred when user click on a data-list / data-table element
 *
 * Allow to display the detail of a record,
 * by going to another page or injecting a sub view
 */
function onSelectRecord(layoutElement: LocoKitLayoutElement, record: LckTableRowOffline) {
  updateFocusPageData(layoutElement)

  /**
   * Find the element related
   */
  const element = getElement(layoutElement.elementKey) as
    | LocoKitElementDataList
    | LocoKitElementDataTable

  /**
   * Preload datasets with chosen record
   */
  if (element.settings.preloadDataset)
    if (element.settings.preloadDatasetKey) {
      datasets.value[element.settings.preloadDatasetKey] = {
        loading: false,
        error: null,
        record
      }
    } else
      console.warn(
        `Element ${layoutElement.elementKey} seems misconfigured (preloadDatasetKey is missing). Please check your configuration : `,
        element.settings
      )
  if (element.settings.provideRouteParam) {
    if (element.settings.provideRouteParamKey) {
      route.query[element.settings.provideRouteParamKey] =
        record[element.settings.provideRouteParamRecordKey]
    }
  }

  if (element.settings.loadDatasetKey) {
    loadDataset(element.settings.loadDatasetKey)
  }

  /**
   * Stash the selected record id in the selection context
   * and reload the datasets depending on it (eg related lists).
   */
  if (element.settings.provideSelectionWildcardKey && record.id) {
    selectionContext.value[element.settings.provideSelectionWildcardKey] = record.id as string
  }
  element.settings.reloadDatasetsOnSelect?.forEach((datasetKey) => loadDataset(datasetKey))

  /**
   * Open the detail view as it is specified in settings :
   * * go to another page
   * * inject the detail page in a sub-view
   */
  if (element.settings.pageDetailId) {
    let path = `/group/${groupId.value}/page/${element.settings.pageDetailId}`
    if (record.id) path += '?id=' + record.id
    if (record.offlineIndex) path += '?offline=true&offlineIndex=' + record.offlineIndex
    router.push(path)
  } else
    console.warn(
      `Element ${layoutElement.elementKey} seems misconfigured (pageDetailId is missing). Please check your configuration : `,
      element.settings
    )

  // how to provide data to the following page ?
  // 'cause we have already the data in the dataset
  // be careful on how to manage data from the local storage for offline usage

  // if we have an id in the URL, we need to check the dataset is set
  // if not, we need to load it ?
  // we need to be careful when user will hit F5 to refresh the page...
  // if we cannot remember the record he was on
}

/**
 * Trigerred when user click on remove button from data-list/table element
 */
async function onRemoveRecord(elementKey: string, record: LckTableRowOffline) {
  try {
    /**
     * Retrieve element settings
     */
    const element = getElement(elementKey) as LocoKitElementFormRecord

    /**
     * TODO: display a popup before removing data
     */
    confirm.require({
      message: t('capel.components.deleteConfirmDialog.message'),
      header: t('capel.components.deleteConfirmDialog.header'),
      icon: 'pi pi-exclamation-circle',
      rejectLabel: t('capel.components.deleteConfirmDialog.rejectLabel'),
      rejectProps: {
        label: t('capel.components.deleteConfirmDialog.rejectLabel'),
        severity: 'secondary',
        outlined: true
      },
      acceptProps: {
        label: t('capel.components.deleteConfirmDialog.acceptLabel'),
        severity: 'danger'
      },
      async accept() {
        await removeData(record, groupId.value)

        if (element.settings.displayToast) {
          toast.add({
            severity: 'info',
            summary: 'Info',
            detail: 'Plongée supprimée !',
            life: 5000
          })
        }

        // reload the current dataset
        loadDataset(element.datasetKey)

        if (element.settings.postAction === 'ROUTER_PUSH') {
          router.push('/group/' + groupId.value + '/page/' + element.settings.nextPageId)
        }
      }
    })
  } catch (error) {
    Sentry.captureException(error)
    toast.add({
      severity: 'error',
      summary: 'Erreur',
      detail: error,
      life: 5000
    })
  }
}

/**
 * Trigerred when user click on duplicate button from data-list/table element
 */
async function onDuplicateRecord(layoutElement: LocoKitLayoutElement, record: LckTableRowOffline) {
  try {
    /**
     * Retrieve element settings
     */
    const element = getElement(layoutElement.elementKey) as LocoKitElementFormRecord

    /**
     * TODO: display a popup before removing data
     */
    confirm.require({
      message: t('capel.components.duplicateConfirmDialog.message'),
      header: t('capel.components.duplicateConfirmDialog.header'),
      icon: 'pi pi-exclamation-circle',
      rejectLabel: t('capel.components.duplicateConfirmDialog.rejectLabel'),
      rejectProps: {
        label: t('capel.components.duplicateConfirmDialog.rejectLabel'),
        severity: 'secondary',
        outlined: true
      },
      acceptProps: {
        label: t('capel.components.duplicateConfirmDialog.acceptLabel'),
        severity: 'info'
      },
      async accept() {
        await onSaveFormRecord(layoutElement, record.data)

        toast.add({
          severity: 'info',
          summary: 'Info',
          detail: 'Plongée dupliquée !',
          life: 5000
        })
      }
    })
  } catch (error) {
    Sentry.captureException(error)
    toast.add({
      severity: 'error',
      summary: 'Erreur',
      detail: error,
      life: 5000
    })
  }
}

/**
 * Update now the page status and column key if needed
 */
function updateFocusPageData(layoutElement: LocoKitLayoutElement) {
  if (layoutElement.focusPageStatus) pageStatus.value = layoutElement.focusPageStatus
  if (layoutElement.focusPageColumnKey) pageColumnKey.value = layoutElement.focusPageColumnKey
}

/**
 * Trigerred when user click on a button
 */
async function onClickButton(layoutElement: LocoKitLayoutElement) {
  updateFocusPageData(layoutElement)
  disableButton(layoutElement)

  /**
   * Find the element related
   */
  const element = getElement(layoutElement.elementKey) as LocoKitElementButton
  if (!element.settings?.action) return

  /**
   * Actions can be asynchronous,
   * so we take each action in order,
   * wait the first to be finished before starting the next one
   * and so on.
   */
  await element.settings.action.reduce(async (previousAction, currentAction) => {
    await previousAction
    switch (currentAction) {
      case 'RELOAD_DATASET':
        if (!element.settings?.actionReloadDatasetKey) {
          console.warn(
            `Element ${layoutElement.elementKey} button seems misconfigured (actionReloadDatasetKey is missing in settings).`,
            element
          )
          return
        } else {
          await Promise.all(
            element.settings?.actionReloadDatasetKey.map((dataset) => loadDataset(dataset))
          )
        }
        break
      case 'RESET_DATASET':
        if (!element.settings?.actionResetDatasetKey) {
          console.warn(
            `Element ${layoutElement.elementKey} button seems misconfigured (actionDatasetKey is missing in settings).`,
            element
          )
          return
        }
        resetDataset(element.settings.actionResetDatasetKey, false)
        break
      case 'ROUTER_PUSH':
        break
      case 'TRIGGER_PROCESS':
        try {
          const actionToast = element.settings?.actionToast
          /**
           * If a toast loading is available, display it
           */
          if (actionToast?.loading)
            toast.add({
              severity: 'info',
              summary: actionToast.loading.title[locale.value],
              detail: actionToast.loading?.message?.[locale.value],
              life: 5000
            })
          const processResult = await lckServices.processRun.create({
            process_id: element.settings?.actionProcessId,
            table_row_id: (
              datasets.value[
              element.settings?.actionProcessDatasetKey as string
              ] as DatasetSingleRecord
            ).record.id,
            waitForOutput: true
          })
          if (processResult.status === 'ERROR') throw new Error('Process failed')
          /**
           * If a toast success is available, display it
           */
          if (actionToast?.success)
            toast.add({
              severity: 'success',
              summary: actionToast.success.title[locale.value],
              detail: actionToast.success.message[locale.value],
              life: 5000
            })
        } catch (error) {
          console.error(error)
          Sentry.captureException(error)
          const actionToast = element.settings?.actionToast
          /**
           * If a toast error is available, display it
           */
          if (actionToast?.error)
            toast.add({
              severity: 'error',
              summary: actionToast.error.title[locale.value],
              detail: actionToast.error.message[locale.value],
              life: 5000
            })
        }

        break
    }
  }, Promise.resolve())

  enableButton(layoutElement)
}

/**
 * Reset a dataset to an empty one,
 * depending if it's a multi one or a single
 *
 * @param datasetKey The key to access the dataset in datasets
 * @param multi Is the dataset a multi one
 */
function resetDataset(datasetKey: string, multi: boolean) {
  if (!multi) {
    datasets.value[datasetKey] = {
      loading: false,
      error: null,
      record: {}
    }
  } else {
    datasets.value[datasetKey] = {
      loading: false,
      error: null,
      records: [],
      total: 0,
      limit: 0,
      skip: 0
    }
  }
}
/**
 * Init all datasets by parsing the page structure,
 * and for each dataset, load with default limit / skip / sort
 *
 * If dataset preload is 'if-not-already-preloaded',
 * check the dataset before
 */
async function initAllDatasets() {
  console.time('initAllDatasets')

  /**
   * First reset all datasets that need it
   */
  Object.keys(pageStructure?.value?.datasets).forEach((datasetKey) => {
    const dataset = pageStructure?.value.datasets[datasetKey]
    if (!dataset.reset) return
    resetDataset(datasetKey, dataset.multi)
  })

  await Promise.all(
    Object.keys(pageStructure?.value?.datasets)
      .filter((datasetKey) => pageStructure?.value.datasets[datasetKey]?.preload)
      .map(async (datasetKey) => {
        const datasetConfiguration = pageStructure?.value.datasets[datasetKey]
        /**
         * Check if needed the dataset is not empty
         */
        if (datasetConfiguration.preload === 'if-not-already-preloaded') {
          if (datasets.value[datasetKey]) return
        }

        await loadDataset(datasetKey)
      })
  )
  console.timeEnd('initAllDatasets')
}
/**
 * On first setup, load all datasets displayed
 */
await initAllDatasets()

/**
 * Compute elements to be displayed according to their conditional display settings
 */
const elementsDisplayed = computed<Record<string, boolean>>(() => {
  const result: Record<string, boolean> = {}
  Object.keys(pageStructure.value.elements).forEach((elementKey) => {
    const element = pageStructure.value.elements[elementKey]
    if (!element.settings?.conditionalDisplay) {
      result[elementKey] = true
      return
    }
    const currentData = (
      datasets.value[element.settings.conditionalDisplayDatasetKey] as DatasetSingleRecord
    )?.record
    if (!currentData) {
      result[elementKey] = false
      return
    }
    result[elementKey] =
      currentData.data?.[element.settings.conditionalDisplayDatasetFieldId] ===
      element.settings.conditionalDisplayDatasetFieldValue
  })
  return result
})

/**
 * Compute if the element need to be displayed,
 * depends on conditional display if set
 */
function isElementDisplayed(layoutElement: LocoKitLayoutElement) {
  /**
   * Check first the conditionalDisplayOnStatusPage layout element prop
   */
  if (
    layoutElement.conditionalDisplayOnStatusPage &&
    !layoutElement.conditionalDisplayOnStatusPage.includes(pageStatus.value)
  )
    return false
  return elementsDisplayed.value?.[layoutElement.elementKey]
}

const exporting = ref(false)
/**
 * Export current view according to current filters in CSV or XLS
 */
async function onClickExport(layoutElement: LocoKitLayoutElement, exportType: 'csv' | 'xls') {
  const currentElement = getElement(layoutElement.elementKey) as
    | LocoKitElementDataList
    | LocoKitElementDataTable
  const currentDataset = getDataset(currentElement.datasetKey)

  if (!currentDataset.table_view_id) return
  exporting.value = true
  switch (exportType) {
    case 'xls':
      await lckHelpers.exportTableRowDataXLS(
        currentDataset.table_view_id,
        {},
        currentElement.datasetKey,
        groupId.value
      )
      break
    case 'csv':
    default:
      await lckHelpers.exportTableRowDataCSV(
        currentDataset.table_view_id,
        {},
        currentElement.datasetKey,
        groupId.value
      )
      break
  }
  exporting.value = false
}

/**
 * When user display offline data or not,
 * or groupId / pageId route params change,
 * reload datasets with default limit / skip / sort
 */
watch([displayOnlyOfflineData, groupId, pageId], () => {
  initAllDatasets()
})

/**
 * React on page structure change
 */
watch(
  [pageStructure],
  () => {
    if (!pageStructure.value) return
    pageStatus.value = pageStructure.value.defaultPageStatus || 'DEFAULT'
    pageColumnKey.value = pageStructure.value.defaultPageColumnKey
  },
  {
    immediate: true
  }
)
</script>

<template>
  <template
    v-for="(layout, layoutKey) in pageStructure?.layouts"
    :key="layoutKey"
  >
    <div
      v-if="layout.type === LAYOUT_TYPE.MONO_COLUMN || layout.type === LAYOUT_TYPE.BI_COLUMN"
      :class="layout.class"
    >
      <div
        v-for="(column, columnIndex) in layout.columns"
        :key="`${layoutKey}-col-${columnIndex}`"
        :class="{
          [column.class as string]: true,
          [column.activeClass as string]: pageColumnKey === column.id
        }"
      >
        <h1
          v-if="column.title"
          class="text-3xl my-2 md:my-4 text-center text-primary"
        >
          {{ column.title[locale] }}
        </h1>
        <div
          v-if="column.offlineDisplay"
          class="flex items-center justify-center w-full my-2"
        >
          {{ t('capel.routes.dives.displayOffline') }}
          <prime-toggle-switch
            v-model="displayOnlyOfflineData"
            class="ml-4"
          />
        </div>
        <!--
          if column is a sub-view,
          we instantiate a new router-view component.

          This will help eg a detailed view to be displayed here
        -->
        <template v-if="column.type === 'sub-view'">
          <router-view />
        </template>

        <div
          v-for="(element, elementIndex) in column.elements.filter(isElementDisplayed)"
          v-else
          :key="`col-0-element-${elementIndex}`"
          :class="element.class"
        >
          <!-- Text components -->
          <text-block
            v-if="getElement(element.elementKey).type === ELEMENT_TYPE.TEXT_BLOCK"
            v-bind="getTextBlockInput(element.elementKey)"
            :class="getElement(element.elementKey).class"
          />
          <!-- Text components -->
          <text-block
            v-else-if="getElement(element.elementKey).type === ELEMENT_TYPE.TEXT_TITLE"
            v-bind="getTextBlockInput(element.elementKey)"
            :class="getElement(element.elementKey).class"
          />

          <text-block
            v-else-if="getElement(element.elementKey).type === ELEMENT_TYPE.DATA_TITLE"
            v-bind="getDataTitleInput(element.elementKey)"
            :class="getElement(element.elementKey).class"
          />

          <!-- Data components -->
          <template
            v-else-if="
              [ELEMENT_TYPE.DATA_LIST, ELEMENT_TYPE.DATA_TABLE].includes(
                getElement(element.elementKey).type
              )
            "
          >
            <div
              v-if="getElement(element.elementKey).settings?.displayFilters !== false"
              class="flex justify-between items-center"
            >
              <filter-overlay
                class="mb-2"
                v-bind="getFilterOverlayInput(element.elementKey)"
                @submit="onSubmitFilters(element.elementKey, $event)"
                @reset="onResetFilters(element.elementKey)"
              />

              <dropdown-button
                v-if="getElement(element.elementKey).settings?.exportAllowed"
                :label="t('capel.components.dropdownButton.label')"
                icon-pos="right"
                :model="[
                  {
                    label: t('capel.components.dropdownButton.labelCsv'),
                    icon: 'pi pi-file',
                    command: () => {
                      onClickExport(element, 'csv')
                    }
                  },
                  {
                    label: t('capel.components.dropdownButton.labelXls'),
                    icon: 'pi pi-file-excel',
                    command: () => {
                      onClickExport(element, 'xls')
                    }
                  }
                ]"
                :icon="exporting ? 'pi pi-spin pi-spinner' : 'pi pi-download'"
                button-class="p-button-secondary"
              />
            </div>
            <data-table
              v-if="getElement(element.elementKey).type === ELEMENT_TYPE.DATA_TABLE"
              v-bind="getDataTableInput(element.elementKey)"
              :class="getElement(element.elementKey).class"
              @page="onPage(element.elementKey, $event)"
              @select-record="onSelectRecord(element, $event)"
              @remove-record="onRemoveRecord(element.elementKey, $event)"
              @duplicate-record="onDuplicateRecord(element, $event)"
            />
            <data-list
              v-else-if="getElement(element.elementKey).type === ELEMENT_TYPE.DATA_LIST"
              v-bind="getDataListInput(element.elementKey)"
              :class="getElement(element.elementKey).class"
              :layout="(getElement(element.elementKey) as LocoKitElementDataList)?.settings?.layout"
              @page="onPage(element.elementKey, $event)"
              @select-record="onSelectRecord(element, $event)"
              @remove-record="onRemoveRecord(element.elementKey, $event)"
              @duplicate-record="onDuplicateRecord(element, $event)"
            />
          </template>

          <!-- Quick filters -->
          <quick-filters
            v-else-if="getElement(element.elementKey).type === ELEMENT_TYPE.QUICK_FILTERS"
            v-bind="getQuickFiltersInput(element.elementKey)"
            :class="getElement(element.elementKey).class"
            @submit="onSubmitQuickFilters(element.elementKey, $event)"
          />

          <!-- Record components -->
          <form-record
            v-else-if="getElement(element.elementKey).type === ELEMENT_TYPE.FORM_RECORD"
            v-bind="getFormRecordInput(element)"
            :class="getElement(element.elementKey).class"
            @save="(...args) => onSaveFormRecord(element, ...args)"
            @complete="onComplete"
            @delete="onRemoveRecord(element.elementKey, $event)"
            @update-field="(...args) => onUpdateFieldFormRecord(element, ...args)"
          />

          <prime-button
            v-else-if="getElement(element.elementKey).type === ELEMENT_TYPE.BUTTON"
            v-bind="getButtonInput(element)"
            @click="onClickButton(element)"
          />

          <data-field
            v-else-if="getElement(element.elementKey).type === ELEMENT_TYPE.DATA_FIELD"
            v-bind="getDataFieldInput(element)"
          />

          <div v-else>
            Élément non implémenté :

            {{ element }}
          </div>
        </div>
      </div>
    </div>
    <div
      v-else
      :class="layout.class"
    >
      Layout not yet implemented
    </div>
  </template>
</template>
